import React from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { theme } from '../../../../theme/theme'
import { FindFriends } from '../../../../components/society/FindFriends'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { getMatches } from '../../../../components/society/FindFriends/helpers'
import { ErrorView } from '../../../error/ErrorView'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { LinkButton } from '../../../../components/common/LinkButton'
import { unitQueries } from '../../../../api/units'
import { userQueries } from '../../../../api/users'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { useAuth } from '../../../../hooks/useAuth'
import { uniqueBy } from '../../../../helpers/array'

export const useGetResidentUsersForSociety = (
  societyId?: string
): { users: IUserModel[]; isLoading: boolean } => {
  const { data: units, isLoading: isUnitsLoading } = useQuery(
    unitQueries.getUnitsPerSociety(societyId)
  )
  const residentsList = units?.flatMap((unit) => unit.residentsList || [])
  const { data: residentUsers, pending: usersLoading } = useQueries({
    queries:
      residentsList?.map((resident) => userQueries.getUser(resident.userId)) ||
      [],
    combine: (results) => {
      return {
        data: results.flatMap((query) => query.data || []),
        pending: results.some((result) => result.isPending),
      }
    },
  })
  const isLoading = usersLoading || isUnitsLoading

  return { users: residentUsers, isLoading }
}

export const FindFriendsView = (): React.ReactNode => {
  const { userId } = useAuth()
  const { society, isLoading: isSocietyLoading } = useCurrentSociety()
  const { translate } = useAppTranslation()
  useDocumentTitle(
    reverseDocumentTitle('society:find-friends', {
      '{{ societyName }}': society?.name,
    })
  )
  const { users: residentUsers, isLoading: isUnitsLoading } =
    useGetResidentUsersForSociety(society?._id)

  const { data: user } = useQuery(userQueries.getUser(userId))

  const uniqueUsers = uniqueBy('_id', residentUsers)
  const categoryMatches = getMatches(uniqueUsers, user)

  const loading = isUnitsLoading || isSocietyLoading

  const errorView: ErrorView | undefined =
    !loading && categoryMatches.length === 0
      ? {
          title: translate('findFriendsView.errorView.noMatches.title'),
          subtitle: translate('findFriendsView.errorView.noMatches.subtitle'),
          illustration: IllustrationChoices.CONVERSATION,
          titleWrapperClassName: 'mt-4',
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase title={translate('findFriendsView.title')} loading={loading}>
        <p style={theme.textVariants.base} className="mb-4">
          {translate('findFriendsView.description')}
        </p>
        <p style={theme.textVariants.base} className="mb-8">
          {translate('findFriendsView.extra')}
        </p>
        <LinkButton
          urlIsExternal={false}
          url={reverseUrl('settings-my-info')}
          label={translate('findFriendsView.updateYourProfile')}
        />
        {errorView ? (
          <ErrorView {...errorView} />
        ) : (
          <FindFriends categoryMatches={categoryMatches} />
        )}
      </ViewBase>
    </ErrorBoundary>
  )
}
